import { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Plus, TrashSimple, Pulse } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess, REQUIRES } from "@/lib/plans";
import { Paywall } from "@/components/PlanGate";

export default function Tracking() {
  const { user } = useAuth();
  const hasAccess = canAccess(user, REQUIRES.TRACKING);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    weight_kg: "",
    body_fat_pct: "",
    measurements: { waist: "", hip: "", chest: "", arm: "", thigh: "" },
    observations: "",
  });

  const load = useCallback(async () => {
    if (!hasAccess) return;
    try {
      const { data } = await api.get("/tracking");
      setEntries(data);
    } catch (err) {
      console.error("[Tracking] load failed:", err);
      toast.error("Error al cargar seguimiento");
    }
  }, [hasAccess]);
  useEffect(() => { load(); }, [load]);

  if (!hasAccess) {
    return (
      <Layout>
        <section className="border-b border-as-border bg-as-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <p className="as-label text-as-green mb-3">MÓDULO 5 · SEGUIMIENTO</p>
            <h1 className="as-heading text-5xl text-white">MIDE TU <span className="text-as-green">PROGRESO.</span></h1>
          </div>
        </section>
        <Paywall planName="AS EVOLUTION" />
      </Layout>
    );
  }

  const submit = async () => {
    try {
      const payload = {
        ...form,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        body_fat_pct: form.body_fat_pct ? Number(form.body_fat_pct) : null,
        measurements: Object.fromEntries(
          Object.entries(form.measurements).filter(([_, v]) => v).map(([k, v]) => [k, Number(v)])
        ),
      };
      await api.post("/tracking", payload);
      toast.success("Registro añadido");
      setOpen(false);
      setForm({ ...form, weight_kg: "", body_fat_pct: "", observations: "" });
      load();
    } catch (err) {
      console.error("[Tracking] submit failed:", err);
      toast.error("Error al guardar");
    }
  };

  const remove = async (id) => {
    await api.delete(`/tracking/${id}`);
    load();
  };

  const chartData = [...entries]
    .reverse()
    .filter((e) => e.weight_kg)
    .map((e) => ({ date: e.date?.slice(5), weight: e.weight_kg, bf: e.body_fat_pct }));

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex justify-between items-end flex-wrap gap-4">
          <div>
            <p className="as-label text-as-green mb-3">MÓDULO 5 · SEGUIMIENTO</p>
            <h1 className="as-heading text-5xl text-white">
              MIDE TU <span className="text-as-green">PROGRESO.</span>
            </h1>
          </div>
          <button onClick={() => setOpen(true)} className="as-btn-primary" data-testid="add-tracking-btn">
            <Plus size={18} weight="bold" /> Nuevo registro
          </button>
        </div>
      </section>

      <section className="bg-as-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {chartData.length >= 2 && (
            <div className="as-card mb-8" data-testid="tracking-chart">
              <h3 className="as-heading text-xl text-white mb-4">EVOLUCIÓN PESO</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#2A2A2A" />
                  <XAxis dataKey="date" stroke="#A0A0A0" fontSize={11} />
                  <YAxis stroke="#A0A0A0" fontSize={11} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0D0D0D", border: "1px solid #39FF14", borderRadius: 0 }}
                    labelStyle={{ color: "#39FF14" }}
                  />
                  <Line type="monotone" dataKey="weight" stroke="#39FF14" strokeWidth={2} dot={{ r: 4, fill: "#39FF14" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="as-card">
            <h3 className="as-heading text-xl text-white mb-4">HISTORIAL ({entries.length})</h3>
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <Pulse size={48} weight="duotone" color="#39FF14" className="mx-auto mb-3" />
                <p className="text-as-gray">Sin registros aún. Comienza con tu primer pesaje.</p>
              </div>
            ) : (
              <div className="divide-y divide-as-border">
                {entries.map((e) => (
                  <div key={e.id} className="py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`tracking-${e.id}`}>
                    <div>
                      <p className="text-white font-semibold">{e.date}</p>
                      <p className="text-xs text-as-gray mt-1">
                        {e.weight_kg ? `${e.weight_kg}kg` : ""}
                        {e.body_fat_pct ? ` · ${e.body_fat_pct}% grasa` : ""}
                        {e.observations ? ` · ${e.observations}` : ""}
                      </p>
                    </div>
                    <button onClick={() => remove(e.id)} className="text-as-gray hover:text-red-500" data-testid={`delete-tracking-${e.id}`}>
                      <TrashSimple size={20} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-as-surface border-2 border-as-green max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-as-border">
              <h3 className="as-heading text-2xl text-white">NUEVO REGISTRO</h3>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              <Field label="Fecha">
                <input className="as-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tracking-date" />
              </Field>
              <Field label="Peso (kg)">
                <input className="as-input" type="number" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="tracking-weight" />
              </Field>
              <Field label="% Grasa">
                <input className="as-input" type="number" step="0.1" value={form.body_fat_pct} onChange={(e) => setForm({ ...form, body_fat_pct: e.target.value })} />
              </Field>
              <Field label="Cintura (cm)">
                <input className="as-input" type="number" value={form.measurements.waist} onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, waist: e.target.value } })} />
              </Field>
              <Field label="Cadera (cm)">
                <input className="as-input" type="number" value={form.measurements.hip} onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, hip: e.target.value } })} />
              </Field>
              <Field label="Brazo (cm)">
                <input className="as-input" type="number" value={form.measurements.arm} onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, arm: e.target.value } })} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Observaciones">
                  <textarea className="as-input min-h-[80px]" value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} data-testid="tracking-notes" />
                </Field>
              </div>
            </div>
            <div className="p-6 border-t border-as-border flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="as-btn-secondary">Cancelar</button>
              <button onClick={submit} className="as-btn-primary" data-testid="save-tracking-btn">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="as-label block mb-2">{label}</span>
    {children}
  </label>
);
